import { useFormik } from "formik"
import { useEffect } from "react"
import useDebounce from "./useDebounce"
import * as Yup from "yup"

/**
 * Default validation schema for table controller
 */
export const defaultTableSchema = Yup.object().shape({
  searchTerm: Yup.string(),
  dateRange: Yup.object().shape({
    startDate: Yup.date().nullable(),
    endDate: Yup.date().nullable(),
  }),
  pagination: Yup.object().shape({
    page: Yup.number().min(1).default(1),
    limit: Yup.number().min(1).default(10),
  }),
  sort: Yup.object().shape({
    field: Yup.string(),
    order: Yup.string().oneOf(["", "asc", "desc"]).default("asc"),
  }),
  filters: Yup.object(),
  search: Yup.object(),
})

/**
 * A custom hook for managing table state including search, filters, sorting, and pagination
 * @param {Object} config - Configuration object for the hook
 * @param {Object} config.initialValues - Initial values for the form
 * @param {Object} config.validationSchema - Additional Yup validation schema to merge with default
 * @param {Function} config.onSubmit - Optional submit handler
 * @returns {Object} Table controller methods and state
 */
const useTableController = ({
  initialValues = {},
  validationSchema = null,
  onSubmit = () => {},
}) => {
  // Initialize search with debounce
  const [searchInput, setSearchInput, debouncedSearch] = useDebounce()

  // Initialize formik with default values
  const formik = useFormik({
    initialValues: {
      searchTerm: "",
      dateRange: {
        startDate: null,
        endDate: null,
      },
      pagination: {
        page: 1,
        limit: 10,
      },
      sort: {
        field: "",
        order: "",
      },
      filters: {},
      search: {},
      ...initialValues,
    },
    validationSchema:
      validationSchema && typeof validationSchema === "object"
        ? defaultTableSchema.concat(validationSchema)
        : defaultTableSchema,
    onSubmit,
  })

  // Update formik search term when debounced search changes
  useEffect(() => {
    formik.setFieldValue("searchTerm", debouncedSearch)
    formik.submitForm()
  }, [debouncedSearch])

  /**
   * Handle table state changes (sort, filter, search, pagination)
   * @param {Object} changes - Object containing changes to apply
   */
  const handleTableChange = changes => {
    // Update formik values
    Object.entries(changes).forEach(([key, value]) => {
      formik.setFieldValue(key, value)
    })

    // Submit form to trigger API call
    formik.submitForm()
  }

  /**
   * Handle date range changes
   * @param {Object} newValue - New date range value
   */
  const handleDateRangeChange = newValue => {
    formik.setFieldValue("dateRange", newValue)
    formik.submitForm()
  }

  /**
   * Handle any field change in formik
   * @param {string} fieldName - Name of the field to update
   * @param {any} value - New value for the field
   */
  const handleFieldChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value)
    formik.submitForm()
  }

  return {
    formik,
    searchInput,
    setSearchInput,
    handleTableChange,
    handleDateRangeChange,
    handleFieldChange,
  }
}

export default useTableController
